import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Avatar,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Card,
  CircularProgress,
} from '@mui/material';
import {
  Logout,
  Campaign,
  CloudUpload,
  Delete,
  Send,
  ArrowBack,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { createRequest } from '../services/dataServiceWrapper';
import { uploadFiles } from '../services/fileService';
import { getGraphClient } from '../services/graphService';
import { sendApproverNotification, sendSubmitterConfirmation } from '../services/emailService';
import { createNotification, NotificationType } from '../services/notificationService';

// Validation schema
const schema = yup.object({
  marketingSubject: yup.string().required('Marketing subject is required'),
  materialsNeeded: yup.string().required('Materials needed is required'),
  keywordsAndTone: yup.string().required('Keywords and tone are required'),
  budget: yup.string().required('Budget is required'),
  dateRequired: yup.date().required('Date required is required').nullable(),
  approver: yup.string().email('Invalid email').required('Approver is required'),
});

// MR_Approvers group ID
const MR_APPROVERS_GROUP_ID = 'e4806c56-b055-478c-ae42-0c19a93b88ed';

const MarketingRequest = () => {
  const { accounts, instance } = useMsal();
  const navigate = useNavigate();
  const account = accounts[0];
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [loadingApprovers, setLoadingApprovers] = useState(true);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      marketingSubject: '',
      materialsNeeded: '',
      keywordsAndTone: '',
      budget: '',
      dateRequired: null,
      approver: '',
    },
  });

  useEffect(() => {
    loadApprovers();
  }, [account]);

  const loadApprovers = async () => {
    try {
      const graphClient = getGraphClient(instance, account);

      // Get members of MR_Approvers group
      const response = await graphClient
        .api(`/groups/${MR_APPROVERS_GROUP_ID}/members`)
        .select('id,displayName,mail,userPrincipalName')
        .top(999)
        .get();

      const approverList = response.value
        .filter(member => member['@odata.type'] === '#microsoft.graph.user')
        .map(member => ({
          id: member.id,
          name: member.displayName || member.userPrincipalName,
          email: member.mail || member.userPrincipalName,
        }));

      setApprovers(approverList);
      console.log('Loaded MR_Approvers for marketing request:', approverList);
    } catch (error) {
      console.error('Error loading approvers:', error);
      toast.error('Failed to load approvers from Azure AD');
    } finally {
      setLoadingApprovers(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setFiles((prev) => [...prev, ...acceptedFiles]);
    },
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/msword': ['.doc', '.docx'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
  });

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const userEmail = account?.email || account?.username || 'current.user@acom.edu.au';
      const userName = account?.name || userEmail.split('@')[0];
      const selectedApprover = approvers.find(a => a.email === data.approver);

      const marketingRequest = {
        id: Date.now().toString(),
        type: 'marketing',
        title: `Marketing Request - ${data.marketingSubject}`,
        submittedBy: userEmail,
        submitterName: userName,
        submittedDate: new Date().toISOString(),
        status: 'pending',
        approver: data.approver,
        approverName: selectedApprover?.name || data.approver,

        // Marketing-specific fields
        marketingSubject: data.marketingSubject,
        materialsNeeded: data.materialsNeeded,
        keywordsAndTone: data.keywordsAndTone,
        budget: data.budget,
        dateRequired: data.dateRequired.toISOString(),

        // File attachments - empty, will be uploaded separately
        documents: [],
      };

      const createdRequest = await createRequest(marketingRequest);

      // Upload files if any (this will create entries in request_documents table)
      if (files.length > 0) {
        try {
          await uploadFiles(createdRequest.id, files, userEmail);
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          // Continue anyway - request is created
        }
      }

      // Send email notification to approver
      await sendApproverNotification(marketingRequest, data.approver);

      // Send confirmation email to submitter
      await sendSubmitterConfirmation(marketingRequest);

      // Create notification for approver
      createNotification({
        type: NotificationType.APPROVAL_NEEDED,
        userEmail: data.approver,
        recipientEmail: data.approver,
        title: 'New Marketing Request',
        message: `${userName} has submitted a marketing request for ${data.marketingSubject}`,
        requestId: marketingRequest.id
      });

      toast.success('Marketing request submitted successfully!');
      navigate('/my-requests');
    } catch (error) {
      console.error('Error submitting marketing request:', error);
      toast.error('Failed to submit marketing request');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: '/',
    });
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#0078d4' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/acom-logo.png"
              alt="ACOM Logo"
              style={{
                height: '40px',
                marginRight: '16px',
                objectFit: 'contain',
                borderRadius: '8px',
                padding: '4px',
                backgroundColor: 'white'
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Apps Portal
            </Typography>
          </Box>

          <Button
            color="inherit"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>

          <Button
            color="inherit"
            startIcon={<Campaign />}
            onClick={() => navigate('/my-requests')}
            sx={{ mr: 2 }}
          >
            My Requests
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">
              {account?.name || account?.username}
            </Typography>
            <Avatar sx={{ bgcolor: '#7b1fa2' }}>
              {(account?.name || account?.username || 'U')[0].toUpperCase()}
            </Avatar>
            <IconButton color="inherit" onClick={handleLogout}>
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Form Content */}
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ color: '#7b1fa2', fontWeight: 'bold', mb: 3 }}>
            Marketing Request Form
          </Typography>

          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            Submit your marketing requirements to the Senior Team for approval. Once approved, your request will be sent to the
            marketing team for processing.
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* First Row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    What are you marketing?
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    {...register('marketingSubject')}
                    error={!!errors.marketingSubject}
                    helperText={errors.marketingSubject?.message}
                    placeholder="Describe what you want to market..."
                  />
                </Card>

                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    What materials do you need?
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    {...register('materialsNeeded')}
                    error={!!errors.materialsNeeded}
                    helperText={errors.materialsNeeded?.message}
                    placeholder="e.g., Brochures, social media graphics, email templates..."
                  />
                </Card>
              </Box>

              {/* Second Row */}
              <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Keywords and Tone for Marketing
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  {...register('keywordsAndTone')}
                  error={!!errors.keywordsAndTone}
                  helperText={errors.keywordsAndTone?.message}
                  placeholder="Specify keywords, tone, target audience, messaging style..."
                />
              </Card>

              {/* Third Row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Budget
                  </Typography>
                  <TextField
                    fullWidth
                    {...register('budget')}
                    error={!!errors.budget}
                    helperText={errors.budget?.message}
                    placeholder="e.g., $500, TBD, No budget"
                  />
                </Card>

                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Date Required
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Controller
                      name="dateRequired"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.dateRequired,
                              helperText: errors.dateRequired?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Card>

                <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Approver
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    {...register('approver')}
                    error={!!errors.approver}
                    helperText={errors.approver?.message || (loadingApprovers ? 'Loading approvers...' : '')}
                    disabled={loadingApprovers}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="">Select an approver</option>
                    {approvers.map((approver) => (
                      <option key={approver.email} value={approver.email}>
                        {approver.name}
                      </option>
                    ))}
                  </TextField>
                </Card>
              </Box>

              {/* Upload Files */}
              <Card sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Attachments (Optional)
                </Typography>
                <Box
                  {...getRootProps()}
                  sx={{
                    border: '2px dashed #ccc',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: isDragActive ? 'action.hover' : 'transparent',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    minHeight: '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <input {...getInputProps()} />
                  {files.length === 0 ? (
                    <>
                      <Typography>Drag & drop files here, or click to browse</Typography>
                      <Button
                        startIcon={<CloudUpload />}
                        sx={{ mt: 1 }}
                        variant="text"
                      >
                        Attach files
                      </Button>
                    </>
                  ) : (
                    <Box>
                      {files.map((file, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2">{file.name}</Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(index);
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Card>

              {/* Submit Button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading || loadingApprovers}
                  startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                  sx={{
                    px: 6,
                    py: 1.5,
                    bgcolor: '#7b1fa2',
                    '&:hover': {
                      bgcolor: '#6a1b9a',
                    },
                  }}
                >
                  {loading ? 'Submitting...' : loadingApprovers ? 'Loading...' : 'Submit'}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: 'background.paper',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © 2025 ACOM Apps Portal. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default MarketingRequest;
